/**
 * Video Speed Dating API Client
 * Handles Daily.co room creation and token generation
 */

import client from './client';

/**
 * Health check for video service
 * @returns {Promise<{status: string, daily_configured: boolean, daily_domain: string|null}>}
 */
export async function checkVideoHealth() {
  const res = await client.get('/video/health');
  return res.data;
}

/**
 * Check if current user is eligible for video speed dating
 * @returns {Promise<{eligible: boolean, reason: string|null, identity_verified: boolean, verify_url: string|null}>}
 */
export async function checkVideoEligibility() {
  const res = await client.get('/video/speed-dating/eligibility');
  return res.data;
}

/**
 * Create a video room for a speed dating pair
 * @param {Object} options
 * @param {string} [options.room_name] - Optional custom room name
 * @param {number} [options.ttl_seconds] - Room TTL (300-21600)
 * @param {number} [options.max_participants=2] - Max participants
 * @returns {Promise<{room_name: string, room_url: string, exp: number}>}
 */
export async function createVideoRoom(options = {}) {
  const res = await client.post('/video/speed-dating/room', {
    room_name: options.room_name || null,
    ttl_seconds: options.ttl_seconds || null,
    max_participants: options.max_participants || 2,
  });
  return res.data;
}

/**
 * Get a meeting token for a video room
 * @param {string} roomName - The room name to join
 * @param {Object} options
 * @param {number} [options.ttl_seconds] - Token TTL (300-21600)
 * @returns {Promise<{token: string, room_url: string, exp: number}>}
 */
export async function getVideoToken(roomName, options = {}) {
  const res = await client.post('/video/speed-dating/token', {
    room_name: roomName,
    ttl_seconds: options.ttl_seconds || null,
  });
  return res.data;
}

/**
 * Delete a video room (cleanup)
 * @param {string} roomName - The room name to delete
 * @returns {Promise<{deleted: boolean, room_name: string}>}
 */
export async function deleteVideoRoom(roomName) {
  const res = await client.delete(`/video/speed-dating/room/${roomName}`);
  return res.data;
}
